import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { connect, useDispatch } from 'react-redux';
import {
  AppBar,
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Toolbar,
  Typography
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import SearchIcon from '@mui/icons-material/Search';
import { useLocation } from 'react-router-dom';
import {
  ClearGlobalSearch,
  GetGlobalSearchData
} from '../../Redux/Actions/GlobalSearch';
import { socketEmitter } from '../../utils/socket';
import { GlobalContext } from '../../utils/ContextProviders/GlobalContextProvider';
import Avatar from '../../globalcomponents/Avatar';
import UserControls from './UserControls';
import { updateAgentStatus } from '../../Redux/util/Apis/users';
import { setGlobalError } from '../../Redux/Reducer/GlobalError';
import { deleteCookie, formatPhoneNumber } from '../../utils';
import { USER_STATUS_LIST } from './constants';
import { WordmarkIcon } from '../../Media/WordmarkIcon';

interface SearchResult {
  _id?: string;
  name: string;
  phone?: string;
  email?: string;
  stage?: string;
  apartment?: { _id?: string; name?: string };
}

interface TopBarProps {
  history: any;
  GlobalData: { data: SearchResult[] };
}

const PrimaryTopBar: React.FC<TopBarProps> = props => {
  const { userData, isZumaUser, toggleTheme, darkMode } =
    useContext(GlobalContext);

  const [searchResults, setSearchResults] = useState<SearchResult[]>([
    { name: '' }
  ]);
  const [statusColor, setStatusColor] = useState('#808080');
  const [lastClickedStatus, setLastClickedStatus] = useState('');
  const [updatingUserStatus, setUpdatingUserStatus] = useState(false);
  const [userControlsVisible, setUserControlsVisible] = useState(false);
  const [agentStatus, setAgentStatus] = useState('offline');
  const [username, setUsername] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dispatch = useDispatch();
  const location = useLocation();

  const agentAuthId = userData?.user_id;

  const toggleUserStatusHandler = async (
    e: React.MouseEvent,
    updatedStatus: string
  ) => {
    e.preventDefault();
    setLastClickedStatus(updatedStatus);
    setUpdatingUserStatus(true);
    const result = await updateAgentStatus(updatedStatus);
    setUpdatingUserStatus(false);
    if (result?.error) {
      return;
    }
    if (result?.status) {
      setAgentStatus(result.status);
      const statusObj = USER_STATUS_LIST?.find(
        ({ key }) => key === result.status
      );
      setStatusColor(statusObj?.indicatorColor ?? '#808080');
    }
  };

  const signOutClickHandler = (e: React.MouseEvent) => {
    e.preventDefault();
    socketEmitter('leave-room', agentAuthId);
    toggleUserStatusHandler(e, 'offline');
    deleteCookie('rb_access_token');
    window.location.href = '/login';
  };

  const createSocketRoomAndUpdateStatus = (
    updatedStatus: string,
    agentId: string
  ) => {
    setAgentStatus(updatedStatus);
    const statusObj = USER_STATUS_LIST?.find(
      ({ key }) => key === updatedStatus
    );
    setStatusColor(statusObj?.indicatorColor ?? '#808080');
    socketEmitter('create-agent-room', { userId: agentId });
  };

  const searchGlobalData = useCallback(
    (val: string) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      setSearchResults([]);
      dispatch(ClearGlobalSearch(''));
      if (val.length < 3) {
        setIsSearching(false);
        return;
      }
      setIsSearching(true);
      debounceTimer.current = setTimeout(async () => {
        await dispatch(GetGlobalSearchData(val));
        setIsSearching(false);
      }, 300);
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(ClearGlobalSearch(''));
  }, [location.pathname]);

  useEffect(() => {
    if (props?.GlobalData?.data?.length > 0) {
      setSearchResults(props.GlobalData.data);
    }
  }, [props?.GlobalData]);

  const handleSearchSelect = (_event: any, data: SearchResult) => {
    if (!data?._id) return;
    props.history.push({
      pathname: `/inbox/searchLead/${data._id}`,
      state: { from: location.pathname }
    });
  };

  const options: SearchResult[] = searchResults;

  useEffect(() => {
    setUsername(userData?.name);
    createSocketRoomAndUpdateStatus(userData?.status, agentAuthId);
  }, [userData, agentAuthId]);

  return (
    <Box
      sx={{
        display: 'flex',
        position: 'fixed',
        width: 'calc(100% - 65px)',
        zIndex: 99,
        ml: '65px'
      }}
    >
      <AppBar
        position="static"
        sx={{ backgroundColor: 'background.default', boxShadow: 'none' }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: '10px',
            bgcolor: 'background.paper'
          }}
        >
          <WordmarkIcon
            sx={{
              height: 30,
              color: theme =>
                theme.palette.mode === 'dark'
                  ? theme.palette.primary.light
                  : theme.palette.primary.main
            }}
          />
          {isZumaUser && (
            <Autocomplete
              freeSolo
              id="GlobalSearch"
              disableClearable
              autoHighlight
              loading={isSearching}
              options={options}
              getOptionLabel={option => (option as SearchResult).name ?? ''}
              filterOptions={options =>
                options.filter(opt => (opt as SearchResult).name)
              }
              onChange={(event, value) =>
                handleSearchSelect(event, value as SearchResult)
              }
              renderOption={(renderProps, option) => {
                const lead = option as SearchResult;
                const contact = lead.phone
                  ? formatPhoneNumber(lead.phone)
                  : lead.email;
                const meta = [lead.apartment?.name, lead.stage]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <Box
                    component="li"
                    {...renderProps}
                    sx={{
                      '&&': {
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        gap: 0.25,
                        px: 2,
                        py: 1
                      }
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        width: '100%',
                        textAlign: 'left',
                        fontWeight: 600,
                        color: 'text.primary',
                        lineHeight: 1.3
                      }}
                    >
                      {lead.name}
                    </Typography>
                    {contact && (
                      <Typography
                        variant="body2"
                        sx={{
                          width: '100%',
                          textAlign: 'left',
                          color: 'text.secondary',
                          lineHeight: 1.3
                        }}
                      >
                        {contact}
                      </Typography>
                    )}
                    {meta && (
                      <Typography
                        variant="caption"
                        sx={{
                          width: '100%',
                          textAlign: 'left',
                          color: 'text.disabled',
                          lineHeight: 1.3
                        }}
                      >
                        {meta}
                      </Typography>
                    )}
                  </Box>
                );
              }}
              sx={{ width: 400 }}
              renderInput={params => (
                <TextField
                  {...params}
                  onChange={e => searchGlobalData(e.target.value)}
                  placeholder="Search leads"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    type: 'text',
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{ marginLeft: 2, color: 'text.disabled' }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {isSearching && (
                          <CircularProgress size={20} sx={{ marginRight: 2 }} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                      boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.04)'
                    }
                  }}
                />
              )}
            />
          )}
          <Box
            sx={{
              display: 'flex',
              width: 300,
              ml: '10px',
              justifyContent: 'flex-end',
              alignItems: 'center'
            }}
          >
            <Box sx={{ borderRadius: '8px', py: '4px' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  position: 'relative'
                }}
              >
                {isZumaUser && (
                  <Box
                    data-testid="status-indicator"
                    data-status={agentStatus}
                    sx={{
                      position: 'absolute',
                      boxSizing: 'border-box',
                      bottom: -1,
                      right: -1,
                      height: 12,
                      width: 12,
                      borderRadius: '50%',
                      zIndex: 3,
                      border: '1px solid',
                      borderColor: 'background.paper',
                      backgroundColor: statusColor
                    }}
                  />
                )}
                <Avatar
                  size="lg"
                  clickable
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    setUserControlsVisible(prev => !prev);
                  }}
                  image="https://assets.getzuma.com/images/dummy-avatar.svg"
                />
                {userControlsVisible && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 40,
                      right: 0
                    }}
                  >
                    <UserControls
                      currentUserName={username}
                      currentUserStatus={agentStatus}
                      currentTheme={darkMode ? 'dark' : 'light'}
                      onToggleTheme={toggleTheme}
                      onToggleUserStatus={toggleUserStatusHandler}
                      onSignOut={signOutClickHandler}
                      lastClickedStatus={lastClickedStatus}
                      isLoadingUserStatus={updatingUserStatus}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
};

const mapStateToProps = (state: any) => ({
  GlobalData: state.GlobalSearch.GlobalSearchData
});

export default connect(mapStateToProps)(PrimaryTopBar);
